import { observer } from "mobx-react-lite";
import { useMemo } from "react";
import { useTran } from "@/hooks";
import {
  useDashboardFiltersStore,
  useDashboardOverviewStore,
  useDashboardEsgDataTableStore,
} from "../../hooks/useDashboardStore";
import { dashboardDataTableStore } from "../../stores/data-table.store";
import { buildPickerOptions } from "../../utils/hierarchy-picker";
import { sortReportingYearsAscending } from "../../utils/reporting-years";
import { DataTableFilterRow, DataTableFilterChips } from "./DataTableFilters";
import DataTableViewSelector from "./DataTableViewSelector";
import SortBySelector from "./SortBySelector";
import DataTableGrid from "./DataTableGrid";
import { formatReportingYearPeriod } from "@/features/esg/utils/period-constants";

// Sentinel BU option for the company root. The backend maps this id to
// company-root-only scope (isCompanySelected / orgId:null). Must match
// PARENT_COMPANY_ID in api esg-data-table.service.js.
const PARENT_COMPANY_ID = "parent_company";

const buildDimKeys = ({
  view,
  years,
  bus,
  countries,
  buOptions,
  countryOptions,
  availableYears,
}) => {
  if (view === "year") {
    const ys = years.length ? years : availableYears;
    return [...ys].sort((a, b) => a - b);
  }
  if (view === "bu") {
    if (bus.length) return bus;
    return buOptions.map((o) => String(o.id));
  }
  if (view === "country") {
    if (countries.length) return countries;
    return countryOptions.map((o) => String(o.id));
  }
  return [];
};

const buildDimLabelFor =
  ({ view, buOptions, countryOptions, reportingYears }) =>
  (key) => {
    if (view === "year") {
      const reportingYear = reportingYears.find(
        (item) => Number(item?.startYear) === Number(key),
      );
      return reportingYear
        ? formatReportingYearPeriod(reportingYear)
        : String(key);
    }
    if (view === "bu") {
      const match = buOptions.find((o) => String(o.id) === String(key));
      return match?.name ?? String(key);
    }
    if (view === "country") {
      const match = countryOptions.find((o) => String(o.id) === String(key));
      return match?.name ?? String(key);
    }
    return String(key);
  };

const EsgDataTableTab = observer(() => {
  const { t } = useTran();
  const { available } = useDashboardFiltersStore();
  const overview = useDashboardOverviewStore();
  const { search } = overview;
  const store = dashboardDataTableStore;
  const tableStore = useDashboardEsgDataTableStore();

  const handleSearchChange = (value) => {
    overview.setSearch(value);
    tableStore.setPage(1);
  };

  const buOptions = useMemo(
    () => [
      {
        id: PARENT_COMPANY_ID,
        name: available.orgTree?.[0]?.name || t("Parent company"),
        children: [],
      },
      ...buildPickerOptions(available.orgTree, "businessUnit"),
    ],
    [available.orgTree, t],
  );
  const countryOptions = useMemo(
    () => buildPickerOptions(available.orgTree, "country"),
    [available.orgTree],
  );
  const availableYears = useMemo(() => {
    if (available.reportingYears?.length) {
      return sortReportingYearsAscending(available.reportingYears).map((item) =>
        Number(item.startYear),
      );
    }
    if (available.years?.length) return available.years;
    return [2023, 2024, 2025];
  }, [available.reportingYears, available.years]);

  const dimKeys = useMemo(
    () =>
      buildDimKeys({
        view: store.view,
        years: store.filters.years,
        bus: store.filters.bus,
        countries: store.filters.countries,
        buOptions,
        countryOptions,
        availableYears,
      }),
    [
      store.view,
      store.filters.years,
      store.filters.bus,
      store.filters.countries,
      buOptions,
      countryOptions,
      availableYears,
    ],
  );

  const dimLabelFor = useMemo(
    () =>
      buildDimLabelFor({
        view: store.view,
        buOptions,
        countryOptions,
        reportingYears: available.reportingYears,
      }),
    [store.view, buOptions, countryOptions, available.reportingYears],
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <DataTableFilterRow
          store={store}
          availableYears={availableYears}
          reportingYears={available.reportingYears}
          buOptions={buOptions}
          countryOptions={countryOptions}
          searchValue={search}
          onSearchChange={handleSearchChange}
        />
        <DataTableViewSelector value={store.view} onChange={store.setView} />
      </div>
      <DataTableFilterChips
        store={store}
        buOptions={buOptions}
        countryOptions={countryOptions}
        reportingYears={available.reportingYears}
      />
      <DataTableGrid
        view={store.view}
        years={store.filters.years}
        bus={store.filters.bus}
        countries={store.filters.countries}
        buOptions={buOptions}
        countryOptions={countryOptions}
        availableYears={availableYears}
        dimKeys={dimKeys}
        dimLabelFor={dimLabelFor}
        headerRight={
          <SortBySelector
            view={store.view}
            sort={tableStore.sort}
            dimKeys={dimKeys}
            dimLabelFor={dimLabelFor}
            onChange={tableStore.setSort}
            onToggleDirection={tableStore.toggleSortDirection}
          />
        }
      />
    </div>
  );
});

export default EsgDataTableTab;
